import { create } from 'zustand';
import { apiClient } from '@/lib/api-client';
import { AuthState, User, LoginResponse } from '@/types/auth';

export const useAuthStore = create<AuthState>((set) => ({
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  chatgptToken: localStorage.getItem('chatgpt_token'),
  isChatGPTConnected: !!localStorage.getItem('chatgpt_token'),

  // Login action
  login: async (email: string, password: string) => {
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      email,
      password,
    });

    const { accessToken, refreshToken, user } = response.data;

    // Update localStorage
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));

    // Update state
    set({
      user,
      accessToken,
      refreshToken,
      isAuthenticated: true,
    });
  },

  // Logout action
  logout: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await apiClient.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('chatgpt_token');
      localStorage.removeItem('openai_token');

      // Reset state
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        chatgptToken: null,
        isChatGPTConnected: false,
      });
    }
  },

  // Clear session (for forced logout from api-client)
  clearSession: () => {
    // Clear localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('chatgpt_token');
    localStorage.removeItem('openai_token');

    // Reset state
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      chatgptToken: null,
      isChatGPTConnected: false,
    });
  },

  // Refresh auth action
  refreshAuth: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<LoginResponse>('/auth/refresh', {
      refreshToken,
    });

    const { accessToken } = response.data;

    // Update localStorage
    localStorage.setItem('accessToken', accessToken);

    // Preserve user data during token refresh
    const currentUser = localStorage.getItem('user');
    const user = currentUser ? JSON.parse(currentUser) : null;

    // Update state
    set({
      accessToken,
      user, // Preserve user data
      isAuthenticated: true,
    });
  },

  // Set user action
  setUser: (user: User) => {
    set({ user });
  },
}));

// ChatGPT Token Management
export const setChatGPTToken = (token: string) => {
  localStorage.setItem('chatgpt_token', token);
  useAuthStore.setState({ isChatGPTConnected: true, chatgptToken: token });
};

export const clearChatGPTToken = () => {
  localStorage.removeItem('chatgpt_token');
  useAuthStore.setState({ isChatGPTConnected: false, chatgptToken: null });
};

export const getChatGPTToken = () => {
  return localStorage.getItem('chatgpt_token');
};

// Listen for storage changes (token cleared by api-client or other tabs)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // If accessToken was removed, clear the auth state
    if (event.key === 'accessToken' && event.newValue === null) {
      useAuthStore.getState().clearSession();
      // Redirect to login if not already there
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
      }
    }
  });

  // Also check periodically if token exists but auth state is wrong
  setInterval(() => {
    const hasToken = !!localStorage.getItem('accessToken');
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    // If token is gone but state says authenticated, clear session
    if (!hasToken && isAuthenticated) {
      useAuthStore.getState().clearSession();
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
      }
    }
  }, 1000); // Check every second
}
